/**
 * Cursor-paged fetch for the ephemeral blockchain feed, shared by every surface
 * that renders it (the Overview activity panel, an address's Transactions tab).
 *
 * The feed pages differently from the DynamoDB-backed list routes, which is why
 * this is its own hook rather than `useTransactionsQuery`:
 *
 *  - The cursor is TOP-LEVEL (`next_cursor` / `has_more`) rather than nested
 *    under `pagination.next`, because it is not one table offset. It holds a
 *    position per `(provider, chain)` lane plus the hashes already served, so a
 *    lane mid-page and a lane already exhausted both resume correctly.
 *  - Only the POST (address) mode pages. The GET mode answers with each chain's
 *    LATEST block — a snapshot, not a feed — so it has no cursor to follow and
 *    {@link OnchainFeedResult.hasMore} is always false there.
 *
 * Both modes previously fetched exactly one page and dropped the cursor on the
 * floor, so a wallet with more history than `limit` simply appeared to end.
 */
import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { unwrapData } from "@authe/fe-commons/api";
import { useApiClient } from "@authe/ui-core/api";
import type { TxRow } from "@authe/tx-table";
import { feedNextCursor, feedRows, type OnchainFeedResponse } from "./transpose";
import { ONCHAIN_FEED_PATH } from "./constants";

export interface UseOnchainFeedOptions {
  /**
   * Wallet addresses to POST. Empty means the GET preview mode (latest blocks
   * across the backend's default chains), which does not page.
   */
  addresses?: string[];
  /**
   * Pin the chains rather than letting the backend auto-discover them.
   * Discovery tends to answer with Ethereum alone, which makes an L2-active
   * address look single-chain.
   */
  chainIds?: string[];
  /** Rows per page. */
  limit: number;
  /** Ask for the interaction graph alongside the rows. */
  graph?: boolean;
  /** Skip fetching while false. */
  enabled?: boolean;
  /** Extra key segments, for callers whose identity is not just address+chain. */
  queryKey?: readonly unknown[];
  /**
   * How long a fetched page counts as fresh. Defaults to {@link FEED_STALE_MS}.
   *
   * This is what makes flipping a chain filter cheap. `chainIds` is part of the
   * query key, so each selection keeps its own page stack; coming back to one
   * you have already paged through restores every page from cache. With the
   * default `staleTime` of 0 it would ALSO immediately refetch — and an infinite
   * query refetches every loaded page in sequence, so returning to a filter you
   * had paged four deep would fire four requests to redraw what was already on
   * screen.
   */
  staleTime?: number;
  /** How long an unused selection's pages survive. See {@link FEED_GC_MS}. */
  gcTime?: number;
}

/**
 * Long enough that switching between chain filters is instant, short enough
 * that a feed left open does not go quietly out of date. The explicit refresh
 * control bypasses this, so a reader who wants "now" can always ask for it.
 */
export const FEED_STALE_MS = 60_000;

/**
 * Retention for a selection nobody is looking at. Generous because the whole
 * point of the chain filter is moving between chains and back — throwing the
 * pages away after the default five minutes turns a comparison into a refetch.
 */
export const FEED_GC_MS = 30 * 60_000;

export interface OnchainFeedResult {
  /** Every page's rows, oldest page first, in served order. */
  rows: TxRow[];
  /** The raw pages, for callers that project something else (e.g. the graph). */
  pages: OnchainFeedResponse[];
  isLoading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => void;
  /** True when the server reported another page behind the cursor. */
  hasMore: boolean;
  /** Fetch the next page and APPEND it. No-op when there is none in flight. */
  loadMore: () => void;
  /** True while a "Load more" is in flight (distinct from the initial load). */
  isLoadingMore: boolean;
}

export function useOnchainFeed(options: UseOnchainFeedOptions): OnchainFeedResult {
  const {
    addresses,
    chainIds,
    limit,
    graph = false,
    enabled = true,
    queryKey = [],
    staleTime = FEED_STALE_MS,
    gcTime = FEED_GC_MS,
  } = options;
  const client = useApiClient();

  const hasAddresses = (addresses?.length ?? 0) > 0;
  const addressKey = (addresses ?? []).join(",");
  // Sorted so the same chain set spelled in a different order is the same cache
  // entry, rather than silently refetching an identical request.
  const chainKey = [...(chainIds ?? [])].sort().join(",");

  const query = useInfiniteQuery({
    queryKey: ["onchain-feed", addressKey, chainKey, limit, graph, ...queryKey],
    enabled,
    staleTime,
    gcTime,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam, signal }) => {
      if (!hasAddresses) {
        const params = new URLSearchParams({ limit: String(limit) });
        if (graph) params.set("graph", "true");
        return unwrapData(
          await client.get<OnchainFeedResponse>(`${ONCHAIN_FEED_PATH}?${params}`, { signal }),
        );
      }
      return unwrapData(
        await client.post<OnchainFeedResponse>(
          ONCHAIN_FEED_PATH,
          {
            addresses,
            ...(chainIds?.length ? { chain_ids: chainIds } : {}),
            ...(graph ? { graph: true } : {}),
            limit,
            // Omitted on the first page: the body is validated, and an explicit
            // null is a different request from an absent key.
            ...(pageParam ? { cursor: pageParam } : {}),
          },
          { signal },
        ),
      );
    },
    // The GET snapshot has no cursor, so this returns undefined there and the
    // "Load more" affordance never appears.
    getNextPageParam: (last: OnchainFeedResponse | undefined) => feedNextCursor(last) ?? undefined,
  });

  const pages = React.useMemo(
    () => (query.data?.pages ?? []).filter((p): p is OnchainFeedResponse => Boolean(p)),
    [query.data],
  );
  const rows = React.useMemo(() => feedRows(pages), [pages]);

  const fetchNextPage = query.fetchNextPage;
  const loadMore = React.useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);

  return {
    rows,
    pages,
    isLoading: query.isLoading,
    // `isFetchingNextPage` is excluded: a "Load more" must not read as the whole
    // table refreshing, or the rows already on screen flip to a loading state.
    isFetching: query.isFetching && !query.isFetchingNextPage,
    error: query.error,
    refetch: query.refetch,
    hasMore: Boolean(query.hasNextPage),
    loadMore,
    isLoadingMore: query.isFetchingNextPage,
  };
}
